import { Layout } from '~/Layouts';
import { BackButton, Container, TigerBrokersModal } from '~/Common';
import { useCallback, useState, useEffect, createContext } from 'react';
import { Progress, Spinner } from 'theme-ui';
import { useTranslation } from 'react-i18next';
import { getNewEoiUser } from 'features/eoi/eoiAPI';
import { 
    getProfileOptionsAsync, 
    getCountriesAsync, 
} from 'features/theme/themeSlice'
import { toast } from 'utils/toast';
import { connect, useDispatch } from 'react-redux';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useSearchParams } from 'hooks/useSearchParams';
import PersonalDetails from './PersonalDetails';
import EntityDetails from './EntityDetails';
import { getEoiUserAsync, setEoiUser } from 'features/auth/authSlice';
import Finished from './Finished';
import moment from 'moment';

const RegistrationDetailsContext = createContext();

const mapStateToProps = (state) => {
    return { theme: state.theme, user: state.user }
}
const RegistrationDetailsForm = ({theme, match, user}) => {
    const { eoiUser, eoi_status } = user;
    let query = useSearchParams();
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    useDocumentTitle(t('Registration Details'));
    const [profileOptions, setProfileOptions] = useState(theme.profile.options);
    const [existingShareholder, setExistingShareholder] = useState(true);
    const [knowsHin, setKnowsHin] = useState(true);
    const [currentStep, setCurrentStep] = useState(1);
    const [tigerModalOpen, setTigerModalOpen] = useState(false);
    const dealInEoiPhase = moment(eoiUser?.available_from).utc().clone().local().isAfter(moment().utc().clone().local());

    useEffect(() => {
        if (currentStep === 3 && eoiUser?.deal_is_ipo && !Boolean(eoiUser?.is_sophisticated)) {
            setTimeout(() => {
                setTigerModalOpen(true);
            }, 3000);
        }
    }, [currentStep, eoiUser?.deal_is_ipo, eoiUser?.is_sophisticated]);

    const nextStep = (e, skip=0) => {
        if (e) {
            e.preventDefault();
        }

        // If the deal is not open for bidding yet (in EOI phase) skip to step 3
        if (currentStep === 1 && dealInEoiPhase) {
            setCurrentStep(3);
            return;
        }
        
        setCurrentStep(currentStep + 1 + skip);
    };

    useEffect(() => {
        if (eoi_status === 'loading') {
            if (match.params.uuid) {
                dispatch(getEoiUserAsync(match.params.uuid));
            } else if (match.params.slug) {
                const storedEoiUuid = sessionStorage.getItem(`eoiUuid_${match.params.slug}`);
                // If we have a stored UUID for this slug, use it instead of creating new
                if (storedEoiUuid) {
                    dispatch(getEoiUserAsync(storedEoiUuid));
                } else {
                    createNewFromSlug();
                }
            }
        }

        function createNewFromSlug() {
            getNewEoiUser(match.params.slug, query.get('is_sophisticated') ?? 1, query.get('utm_source') ?? '')
                .then((response) => {
                    dispatch(setEoiUser(response));
                    setExistingShareholder(!(response?.data?.alternative_investment || response?.data?.deal_is_ipo));
                    setKnowsHin(!(response?.data?.alternative_investment || response?.data?.deal_is_ipo));
                    sessionStorage.setItem(`eoiUuid_${match.params.slug}`, response?.data?.encrypted_uuid);
                })
                .catch(() => {
                    toast.error(t('eoiUsers.error'), {
                        toastId: 'eoi_reg_details-error',
                    });
                });
        }
    }, [match.params.slug, match.params.uuid, t, query, dispatch, eoi_status]);

    const getOptions = useCallback(() => {
        if (theme.profile.status === 'loading') {
            dispatch(getProfileOptionsAsync());
        }
        if (theme.countries.status === 'loading') {
            dispatch(getCountriesAsync());
        }
    }, [theme, dispatch]);

    useEffect(() => {
        getOptions();
        if (theme.profile.status === 'attempted' && theme.countries.status === 'attempted') {
            setProfileOptions(theme.profile.options);
            setLoading(false);
        }
    }, [theme, getOptions]);

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return <PersonalDetails dealInEoiPhase={dealInEoiPhase} />;
            case 2:
                return <EntityDetails />;
            case 3:
                return <Finished dealInEoiPhase={dealInEoiPhase} />;
            default:
                return <PersonalDetails />;
        }
    };

    const goBack = () => {
        if (dealInEoiPhase && currentStep === 3) {
            setCurrentStep(1);
            return;
        }
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    if (loading || !eoiUser) {
        return <Spinner height="100vh" variant="styles.spinner" m={'auto'} />;
    }

    return (
        <Layout 
            banner={eoiUser?.whitelabel_page && eoiUser?.whitelabel_background_image?.original}
            whitelabelLogo={eoiUser?.whitelabel_page && eoiUser?.whitelabel_logo?.original}
            sellStatement={eoiUser?.whitelabel_page ? eoiUser?.company?.sell_statement : ''}
        >
            <RegistrationDetailsContext.Provider value={{
                eoiUser,
                setEoiUser,
                profileOptions,
                existingShareholder,
                setExistingShareholder,
                knowsHin,
                setKnowsHin,
                loading,
                setLoading,
                nextStep,
                setCurrentStep,
            }}>
                {currentStep !== 1 && 
                    <BackButton onClick={() => goBack()} sx={{
                        position: 'absolute',
                        top: [4,5],
                        left: [2,0],
                        mt: '-10px'
                    }} />
                }
                <Container>
                    <Progress max={1} value={currentStep / 3} mb={5} >
                        {parseFloat(currentStep / 3)}%
                    </Progress>

                    {renderStep()}
                </Container>
                <TigerBrokersModal isOpen={tigerModalOpen} handleClose={() => setTigerModalOpen(false)} />
            </RegistrationDetailsContext.Provider>
            
        </Layout>
        
    )
}

export { RegistrationDetailsContext };
export default connect(mapStateToProps, null)(RegistrationDetailsForm);